import { useEffect, useState } from "react";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { styles } from "../styles";
import BackButton from "../components/BackButton";

/**
 * Each user's employee profile — the source of truth for "who is this" when
 * we show names on notes, messages, signed tickets, the header, etc.
 *
 * Defaults to editing the signed-in user. When targetUser is provided
 * (admin opening another employee from Manage Employees), edits that
 * user's doc instead — same form, different uid + heading + intro copy.
 */
export default function ProfilePage({ user, profile = {}, targetUser, onBack }) {
  // Source of truth for the form: the target if admin-editing someone else,
  // otherwise the signed-in user's own profile.
  const isAdminEdit = !!targetUser;
  const subjectUid = isAdminEdit ? targetUser.uid : user?.uid;
  const subjectEmail = isAdminEdit ? targetUser.email : user?.email;
  const subjectProfile = isAdminEdit ? targetUser.profile || {} : profile;

  // Identity/contact fields are admin-managed. Employees viewing their own
  // profile see read-only inputs — they can't change their own name, phone,
  // emergency contact, etc. To request a change they ask their admin to
  // update it from Manage Employees. This prevents accidental or
  // unauthorized changes to fields that show on signed work orders and
  // payroll records. Self-service for notification preferences + password
  // lives on separate pages and is unaffected.
  const canEditFields = isAdminEdit;

  const [firstName, setFirstName] = useState(subjectProfile.firstName || "");
  const [lastName, setLastName] = useState(subjectProfile.lastName || "");
  const [phone, setPhone] = useState(subjectProfile.phone || "");
  const [jobTitle, setJobTitle] = useState(subjectProfile.jobTitle || "");
  const [street, setStreet] = useState(subjectProfile.street || "");
  const [city, setCity] = useState(subjectProfile.city || "");
  const [state, setStateField] = useState(subjectProfile.state || "");
  const [zip, setZip] = useState(subjectProfile.zip || "");
  const [hireDate, setHireDate] = useState(subjectProfile.hireDate || "");
  const [emergencyName, setEmergencyName] = useState(
    subjectProfile.emergencyName || ""
  );
  const [emergencyPhone, setEmergencyPhone] = useState(
    subjectProfile.emergencyPhone || ""
  );
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  // Re-seed the form when the subject changes (admin switches between users)
  // OR when the underlying profile updates (saved on another tab).
  useEffect(() => {
    setFirstName(subjectProfile.firstName || "");
    setLastName(subjectProfile.lastName || "");
    setPhone(subjectProfile.phone || "");
    setJobTitle(subjectProfile.jobTitle || "");
    setStreet(subjectProfile.street || "");
    setCity(subjectProfile.city || "");
    setStateField(subjectProfile.state || "");
    setZip(subjectProfile.zip || "");
    setHireDate(subjectProfile.hireDate || "");
    setEmergencyName(subjectProfile.emergencyName || "");
    setEmergencyPhone(subjectProfile.emergencyPhone || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subjectUid]);

  const save = async () => {
    if (!subjectUid) return;
    setSaving(true);
    setMsg("");
    try {
      await updateDoc(doc(db, `users/${subjectUid}`), {
        profile: {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          phone: phone.trim(),
          jobTitle: jobTitle.trim(),
          street: street.trim(),
          city: city.trim(),
          state: state.trim(),
          zip: zip.trim(),
          hireDate: hireDate || "",
          emergencyName: emergencyName.trim(),
          emergencyPhone: emergencyPhone.trim(),
          updatedAt: new Date().toISOString(),
          updatedBy: user?.email || "",
        },
      });
      setMsg("Saved.");
    } catch (err) {
      setMsg(`Couldn't save: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const displayLabel = isAdminEdit
    ? `${(subjectProfile.firstName || subjectProfile.lastName)
        ? `${subjectProfile.firstName || ""} ${subjectProfile.lastName || ""}`.trim()
        : subjectEmail || "this employee"}`
    : "";

  return (
    <>
      <BackButton onClick={onBack} />
      <h2>{isAdminEdit ? `Edit Profile — ${displayLabel}` : "My Profile"}</h2>
      <p style={{ fontSize: "12px", color: "#666", margin: "0 0 8px" }}>
        {isAdminEdit
          ? "Edit this employee's profile fields. Saved fields show up on their notes, messages, signed work orders, and the app header."
          : "Your profile information is managed by your organization's admin. To update anything below, ask them to change it from Manage Employees."}
      </p>

      <div style={styles.card}>
        <strong style={{ display: "block", marginBottom: "6px" }}>Name</strong>
        <input
          style={styles.input}
          disabled={!canEditFields}
          placeholder="First name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
        />
        <input
          style={styles.input}
          disabled={!canEditFields}
          placeholder="Last name"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
        />
        <input
          style={styles.input}
          disabled={!canEditFields}
          placeholder="Job title (e.g. HVAC Technician)"
          value={jobTitle}
          onChange={(e) => setJobTitle(e.target.value)}
        />
      </div>

      <div style={styles.card}>
        <strong style={{ display: "block", marginBottom: "6px" }}>Contact</strong>
        <p style={{ fontSize: "12px", color: "#666", margin: "4px 0" }}>
          Email: <strong>{subjectEmail}</strong> (set at account creation)
        </p>
        <input
          style={styles.input}
          disabled={!canEditFields}
          type="tel"
          placeholder="Phone"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
        />
        <input
          style={styles.input}
          disabled={!canEditFields}
          placeholder="Street"
          value={street}
          onChange={(e) => setStreet(e.target.value)}
        />
        <input
          style={styles.input}
          disabled={!canEditFields}
          placeholder="City"
          value={city}
          onChange={(e) => setCity(e.target.value)}
        />
        <input
          style={styles.input}
          disabled={!canEditFields}
          placeholder="State"
          value={state}
          onChange={(e) => setStateField(e.target.value)}
        />
        <input
          style={styles.input}
          disabled={!canEditFields}
          placeholder="ZIP"
          value={zip}
          onChange={(e) => setZip(e.target.value)}
        />
      </div>

      <div style={styles.card}>
        <strong style={{ display: "block", marginBottom: "6px" }}>Employment</strong>
        <label style={{ fontSize: "12px", display: "block", marginTop: "4px" }}>
          Hire date
        </label>
        <input
          style={styles.input}
          disabled={!canEditFields}
          type="date"
          value={hireDate}
          onChange={(e) => setHireDate(e.target.value)}
        />
      </div>

      <div style={styles.card}>
        <strong style={{ display: "block", marginBottom: "6px" }}>Emergency contact</strong>
        <input
          style={styles.input}
          disabled={!canEditFields}
          placeholder="Emergency contact name"
          value={emergencyName}
          onChange={(e) => setEmergencyName(e.target.value)}
        />
        <input
          style={styles.input}
          disabled={!canEditFields}
          type="tel"
          placeholder="Emergency contact phone"
          value={emergencyPhone}
          onChange={(e) => setEmergencyPhone(e.target.value)}
        />
      </div>

      {canEditFields && (
        <button style={styles.button} onClick={save} disabled={saving}>
          {saving ? "Saving..." : "Save Profile"}
        </button>
      )}
      {msg && (
        <p style={{ fontSize: "12px", marginTop: "6px" }}>{msg}</p>
      )}
    </>
  );
}
